// components/Survey/MySurveysPage.jsx — staff view of surveys shared with them
import React from "react";
import { useNavigate } from "react-router-dom";
import { Button, Text } from "@fluentui/react-components";
import { ClipboardTaskListLtr24Regular, ArrowRight20Regular, CheckmarkCircle20Filled, Clock24Regular, LockClosed24Regular, Eye24Regular } from "@fluentui/react-icons";
import { useUserAccess } from "../../context/UserAccessContext";
import { useSurveys } from "../../context/SurveyContext";
import { C, card, StatusChip, EmptyState, LoadingBlock, ErrorBanner } from "./surveyUI";
import { countQuestions, estimateMinutes, formatDate, relativeTime, todayIso } from "./surveyUtils";

function daysLeft(due) {
  if (!due) return null;
  const diff = Math.round((new Date(due + "T12:00:00") - new Date(todayIso() + "T12:00:00")) / 86400000);
  if (diff < 0) return `${Math.abs(diff)} day${diff === -1 ? "" : "s"} overdue`;
  if (diff === 0) return "Due today";
  if (diff === 1) return "Due tomorrow";
  return `${diff} days left`;
}

export default function MySurveysPage() {
  const navigate = useNavigate();
  const { currentUserName } = useUserAccess();
  const { myAssignments, myPendingCount, loading, error, refresh } = useSurveys();

  const todo = myAssignments.filter((a) => a.status === "pending" || a.status === "overdue")
    .sort((a, b) => (a.dueDate || "9999") < (b.dueDate || "9999") ? -1 : 1);
  const done = myAssignments.filter((a) => a.status === "submitted");
  const missed = myAssignments.filter((a) => a.status === "expired");
  const first = (currentUserName || "").split(" ")[0] || "there";

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      {/* Hero */}
      <div style={{ borderRadius: 16, padding: "28px 30px", marginBottom: 22, background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 55%, #0052CC 100%)`, color: "#fff", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", right: -60, top: -80, width: 260, height: 260, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
        <div style={{ position: "absolute", right: 80, bottom: -100, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
        <Text style={{ fontSize: 13, color: "#9CC0FF", fontWeight: 600, display: "block" }}>My Surveys</Text>
        <Text style={{ fontSize: 26, fontWeight: 800, display: "block", marginTop: 4 }}>Hello, {first}</Text>
        <Text style={{ fontSize: 14, color: "#D6E4FF", display: "block", marginTop: 6 }}>
          {loading ? "Checking for surveys…" : myPendingCount
            ? `You have ${myPendingCount} survey${myPendingCount === 1 ? "" : "s"} waiting for your feedback.`
            : "You're all caught up — thank you for sharing your feedback."}
        </Text>
      </div>

      <ErrorBanner error={error} onRetry={refresh} />

      {loading ? <LoadingBlock /> : myAssignments.length === 0 ? (
        <EmptyState icon={ClipboardTaskListLtr24Regular} title="No surveys yet" text="When a survey is shared with you, it will appear here and you'll receive an email with a link." />
      ) : (
        <>
          {todo.length > 0 && (
            <>
              <Text style={{ fontSize: 15, fontWeight: 700, color: C.ink, display: "block", marginBottom: 10 }}>Awaiting your response</Text>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 14, marginBottom: 28 }}>
                {todo.map((a) => {
                  const left = daysLeft(a.dueDate);
                  return (
                    <div key={a.id} style={{ ...card, padding: 0, overflow: "hidden", display: "flex", flexDirection: "column", transition: "box-shadow 0.15s, transform 0.15s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,26,61,0.12)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = card.boxShadow; e.currentTarget.style.transform = "none"; }}
                    >
                      <div style={{ height: 5, background: a.status === "overdue" ? C.danger : `linear-gradient(90deg, ${C.brand}, #00A3C4)` }} />
                      <div style={{ padding: "16px 18px", flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                          {a.survey.category ? <span style={{ fontSize: 11, fontWeight: 700, color: C.brand, textTransform: "uppercase", letterSpacing: 0.6 }}>{a.survey.category}</span> : <span />}
                          <StatusChip status={a.status} />
                        </div>
                        <Text style={{ fontSize: 16, fontWeight: 700, color: C.ink, lineHeight: 1.35 }}>{a.survey.title}</Text>
                        {a.survey.description && (
                          <Text style={{ fontSize: 13, color: C.ink2, lineHeight: 1.55, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{a.survey.description}</Text>
                        )}
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12, color: C.muted, marginTop: "auto", paddingTop: 6 }}>
                          <span>{countQuestions(a.survey.questions)} questions · ~{estimateMinutes(a.survey.questions)} min</span>
                          {a.survey.settings?.anonymous && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><LockClosed24Regular style={{ fontSize: 12 }} /> Anonymous</span>}
                        </div>
                      </div>
                      <div style={{ padding: "12px 18px", borderTop: `1px solid ${C.line}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, background: C.soft }}>
                        <span style={{ fontSize: 12, color: a.status === "overdue" ? C.danger : C.ink2, display: "inline-flex", alignItems: "center", gap: 4, fontWeight: a.status === "overdue" ? 700 : 400 }}>
                          <Clock24Regular style={{ fontSize: 14 }} />
                          {left ? `${left} · ${formatDate(a.dueDate)}` : `Shared ${relativeTime(a.sharedAt)}`}
                        </span>
                        <Button appearance="primary" size="small" icon={<ArrowRight20Regular />} iconPosition="after" onClick={() => navigate(`/my-surveys/${a.id}`)}>
                          Start
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {done.length > 0 && (
            <>
              <Text style={{ fontSize: 15, fontWeight: 700, color: C.ink, display: "block", marginBottom: 10 }}>Completed</Text>
              <div style={{ ...card, marginBottom: 28 }}>
                {done.map((a, i) => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 18px", borderTop: i ? `1px solid ${C.line}` : "none" }}>
                    <CheckmarkCircle20Filled style={{ color: "#2E8540", fontSize: 22, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: 14, fontWeight: 600, color: C.ink, display: "block" }}>{a.survey.title}{a.round > 1 ? <span style={{ fontWeight: 400, color: C.muted }}> · round {a.round}</span> : null}</Text>
                      <Text style={{ fontSize: 12, color: C.muted }}>Submitted {formatDate(a.response.submittedAt)}</Text>
                    </div>
                    <Button appearance="subtle" size="small" icon={<Eye24Regular />} onClick={() => navigate(`/my-surveys/${a.id}`)}>My answers</Button>
                  </div>
                ))}
              </div>
            </>
          )}

          {missed.length > 0 && (
            <>
              <Text style={{ fontSize: 15, fontWeight: 700, color: C.ink2, display: "block", marginBottom: 10 }}>Closed</Text>
              <div style={{ ...card, opacity: 0.8 }}>
                {missed.map((a, i) => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 18px", borderTop: i ? `1px solid ${C.line}` : "none" }}>
                    <LockClosed24Regular style={{ color: C.muted, fontSize: 20, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text style={{ fontSize: 14, color: C.ink2, display: "block" }}>{a.survey.title}</Text>
                      <Text style={{ fontSize: 12, color: C.muted }}>No longer accepting responses</Text>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
